import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Progress, 
  List, 
  Avatar, 
  Rate,
  Tag,
  Space,
  Button,
  DatePicker,
  Select
} from 'antd';
import { 
  StarOutlined, 
  MessageOutlined, 
  TrophyOutlined,
  RiseOutlined,
  UserOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { reviewAnalyticsService } from '../../../services/reviewService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ReviewAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [timeframe, setTimeframe] = useState('30days');

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const stats = await reviewAnalyticsService.getReviewStats();
      setAnalytics(stats);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return '#52c41a';
    if (rating >= 4.0) return '#1890ff';
    if (rating >= 3.0) return '#faad14';
    return '#ff4d4f';
  };

  const getRatingPercentage = (rating) => {
    if (!analytics) return 0;
    return (analytics.ratingDistribution[rating] / analytics.totalReviews) * 100;
  };

  if (loading) {
    return <div>Loading analytics...</div>;
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Review Analytics
            </Title>
            <Text type="secondary">
              Customer feedback and review performance
            </Text>
          </Col>
          <Col>
            <Space>
              <Select
                value={timeframe}
                onChange={setTimeframe}
                style={{ width: 120 }}
              >
                <Option value="7days">Last 7 days</Option>
                <Option value="30days">Last 30 days</Option>
                <Option value="90days">Last 90 days</Option>
                <Option value="1year">Last year</Option>
              </Select>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
              />
            </Space>
          </Col>
        </Row>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Reviews"
              value={analytics.totalReviews}
              prefix={<MessageOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Average Rating"
              value={analytics.averageRating}
              precision={1}
              prefix={<StarOutlined />}
              valueStyle={{ color: getRatingColor(analytics.averageRating) }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="5-Star Reviews"
              value={analytics.ratingDistribution[5]}
              suffix={`/ ${analytics.totalReviews}`}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Response Rate"
              value={85}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Rating Distribution */}
        <Col xs={24} lg={12}>
          <Card title="Rating Distribution">
            <div style={{ padding: '1rem 0' }}>
              {[5, 4, 3, 2, 1].map(rating => (
                <div key={rating} style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Space>
                      <Text strong>{rating}</Text>
                      <StarOutlined style={{ color: '#faad14' }} />
                    </Space>
                    <Text>{analytics.ratingDistribution[rating]} reviews</Text>
                  </div>
                  <Progress
                    percent={getRatingPercentage(rating)}
                    strokeColor={rating >= 4 ? '#52c41a' : rating >= 3 ? '#faad14' : '#ff4d4f'}
                    showInfo={false}
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Recent Reviews */}
        <Col xs={24} lg={12}>
          <Card title="Recent Reviews">
            <List
              dataSource={analytics.recentReviews}
              renderItem={review => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={<UserOutlined />}
                        style={{ backgroundColor: '#1890ff' }}
                      />
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{review.customerName || 'Anonymous'}</Text>
                        <Rate disabled value={review.rating} style={{ fontSize: '0.8rem' }} />
                      </div>
                    }
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                          {dayjs(review.createdAt).format('MMM DD, YYYY')}
                        </Text>
                        <br />
                        <Text style={{ fontSize: '0.9rem' }}>
                          {review.review.length > 100 
                            ? `${review.review.substring(0, 100)}...` 
                            : review.review
                          }
                        </Text>
                        <br />
                        <Tag color="blue" style={{ marginTop: '0.25rem' }}>
                          {review.serviceType}
                        </Tag>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Review Trends */}
      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        <Col xs={24}>
          <Card title="Review Trends">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: '#52c41a', margin: 0 }}>
                    +12%
                  </Title>
                  <Text type="secondary">Reviews this month</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: '#1890ff', margin: 0 }}>
                    4.8
                  </Title>
                  <Text type="secondary">Average rating this month</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: '#faad14', margin: 0 }}>
                    24h
                  </Title>
                  <Text type="secondary">Average response time</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <Space>
          <Button type="primary" icon={<MessageOutlined />}>
            Send Review Requests
          </Button>
          <Button icon={<CalendarOutlined />}>
            Schedule Follow-ups
          </Button>
          <Button icon={<RiseOutlined />}>
            Export Report
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default ReviewAnalytics;
